import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/store/ProductCard";
import { Reveal } from "@/components/store/Reveal";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["All", "Face", "Body", "Kits"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const category = params.get("category") || "All";

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/products", { params: { category, search: search || undefined, sort } })
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, search, sort]);

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal>
          <p className="overline text-terracotta mb-3">The Collection</p>
          <h1 className="font-serif text-5xl md:text-7xl text-espresso tracking-tight mb-8">Shop skincare</h1>
        </Reveal>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 mb-12 border-b border-sandline pb-6">
          <div className="flex flex-wrap gap-2" data-testid="category-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-tab-${c}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === c ? "bg-espresso text-bone" : "bg-sand text-bark hover:bg-sandline"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-3 items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-bark" />
              <Input
                data-testid="shop-search"
                placeholder="Search products"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-full sm:w-52 rounded-full border-sandline bg-bone focus-visible:ring-terracotta"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-40 rounded-full border-sandline bg-bone" data-testid="shop-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-sand rounded-2xl aspect-[4/5]" />
                <div className="h-4 bg-sand rounded mt-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-bark py-20 text-center" data-testid="shop-empty">No products found.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="shop-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
